<template lang="pug">
div(v-if="isShown")
    v-autocomplete(
        :label='label'
        v-model='localValue'
        :items='localItems'
        :item-text="itemText"
        :item-value="itemValue"
        :clearable="clearable"
        :prepend-icon="prependIcon"
        :multiple="multiple"
        :disabled="isDisabled"
        :hint="hint"
        :loading="loading"
        :chips="chips"
        :deletable-chips="deletableChips"
        :auto-close-on-select="autoCloseOnSelect"
        :menu-props="{ maxHeight: maxHeight }"
        :item-disabled="headerTest"
        :filter="customSearchFilter"
        @change="onChange"
        ref="autocomplete"

    )
        template(v-slot:item="{ item }")
            template(v-if="item.isHeader")
                b(style="color: black;") {{ item.headerName }}
            template(v-else)
                v-row(style="margin: 0px;")
                    div(v-if="multiple" :style="{'margin-top': itemCheckboxMargin}")
                        v-checkbox(
                            style="pointer-events: none;"
                            :dense="dense"
                            :input-value="isValueSelected(item)"
                        )

                    div(:style="{'margin-top': itemTextMargin}")
                        | {{ item[itemText] }}
</template>

<script lang="ts">
import Vue from 'vue';

// Components

export default Vue.extend({
    data() {
        return {
            localItems: [],
            localItemMap: null,
            localItemShownMap: null,
            localValue: Array.isArray(this.value) ? this.value : (this.multiple ? [] : this.value),
        }
    },
    methods: {
        constructItems() {
            if (this.items.length === 0) {
                return;
            }

            this.localItems = [];
            const itemMap = new Map();
            const shownMap = new Map();
            let keys = [];

            // defaults to 'defaultGroup' if this.items[i][this.itemDividerVariable] is null
            for (let i = 0; i < this.items.length; i++) {
                const result = itemMap.get(this.items[i][this.itemDividerVariable] || this.defaultGroup);

                if (result) {
                    result.push(this.items[i]);
                } else {
                    itemMap.set(this.items[i][this.itemDividerVariable] || this.defaultGroup, [ this.items[i] ]);
                    shownMap.set(this.items[i][this.itemDividerVariable] || this.defaultGroup, true);
                    keys.push(this.items[i][this.itemDividerVariable] || this.defaultGroup);
                }
            }

            if (this.headerSort === 'asc') {
                keys = [...keys].sort((a, b) => a?.localeCompare(b));
            } else if (this.headerSort === 'desc') {
                keys = [...keys].sort((a, b) => b?.localeCompare(a));
            }

            // will show the default column first if enabled
            if (this.showDefaultColumnFirst && keys.includes(this.defaultGroup)) {
                keys = [this.defaultGroup, ...keys.filter(item => item !== this.defaultGroup)];
            }

            // instantiates the localItems array
            for (let i = 0; i < keys.length; i++) {
                const result = itemMap.get(keys[i]);

                if (!result) {
                    continue;
                }

                this.localItems.push({ headerName: keys[i], isHeader: true, });
                this.localItems.push(...result);
            }

            this.localItemMap = itemMap;
            this.localItemShownMap = shownMap;
        },
        customSearchFilter(item, query, itemText) {

            // special case if item is a header
            if (item.isHeader) {
                // allows for it to be disabled
                if (this.allowHeadersInSearch) {
                    // check if header name itself matches the search query (computed once)
                    const headerMatches = item.headerName.toLowerCase().includes(query.toLowerCase());
                    if (headerMatches) {
                        this.localItemShownMap.set(item.headerName, true);
                        return true;
                    }

                    this.localItemShownMap.set(item.headerName, false);

                    // if any child elements would be shown, show the header as well
                    const result = this.localItemMap.get(item.headerName);
                    for (let i = 0; i < result.length; i++) {
                        if (result[i][this.itemText].toLowerCase().includes(query.toLowerCase())) {
                            return true;
                        }
                    }
                }

                return false;
            } else if (this.localItemShownMap.get(item[this.itemDividerVariable]) === true) {
                return true;
            }

            // basic search functionality for normal items
            return itemText.toLowerCase().includes(query.toLowerCase());
        },
        headerTest(item) {
            return item.isHeader;
        },
        isValueSelected(item) {
            if (!this.multiple) {
                return false;
            }

            // a value of null will add the entire object to the list instead of a single variable
            if (this.itemValue === null) {
                return this.localValue.includes(item);
            }

            // get variable
            const parsedValue = item[this.itemValue];
            return (this.localValue || []).includes(parsedValue) && parsedValue !== null;
        },
        onChange(value) {
            this.$nextTick(() => {
                this.$emit('update', value);

                // Auto-close dropdown after selection if enabled and in multi-select mode
                if (this.autoCloseOnSelect && this.multiple && this.$refs.autocomplete) {
                    this.$refs.autocomplete.blur();
                }
            })
        }
    },
    computed: {
        isDisabled() {
            return !this.isShown;
        },
        itemCheckboxMargin() {
            return (this.dense) ? '3px' : '0px';
        },
        itemTextMargin() {
            if (!this.multiple) {
                return '0px';
            }

            return (this.dense) ? '11px' : '20px';
        },
    },
    components: {

    },
    props: {
        allowHeadersInSearch: {
            type: Boolean,
            required: false,
            default: true,
        },
        autoCloseOnSelect: {
            type: Boolean,
            required: false,
            default: false,
        },
        chips: {
            type: Boolean,
            required: false,
            default: false,
        },
        clearable: {
            type: Boolean,
            required: false,
            default: false,
        },
        defaultGroup: {
            type: String,
            required: false,
            default: "Account Level", // Default name for when the 'itemDividerVariable' is null
        },
        deletableChips: {
            type: Boolean,
            required: false,
            default: false,
        },
        dense: {
            type: Boolean,
            required: false,
            default: false,
        },
        headerSort: {
            type: String,
            required: false,
        },
        hint: {
            type: String,
            required: false,
            default: "",
        },
        isShown: {
            type: Boolean,
            required: false,
            default: true,
        },
        itemDividerVariable: {
            type: String,
            required: true,
        },
        items: {
            type: Array,
            required: true,
        },
        itemText: {
            type: String,
            required: true,
        },
        itemValue: {
            type: String,
            required: false,
            default: '',
        },
        label: {
            type: String,
            required: false,
            default: '',
        },
        loading: {
            type: Boolean,
            required: false,
            default: false,
        },
        maxHeight: {
            type: Number,
            required: false,
            default: 400,
        },
        multiple: {
            type: Boolean,
            required: false,
            default: false,
        },
        prependIcon: {
            type: String,
            required: false,
            default: '',
        },
        showDefaultColumnFirst: {
            type: Boolean,
            required: false,
            default: true,
        },
        value: {
            required: true,
        }
    },
    watch: {
        localValue(newValue) {
            this.$emit('input', newValue);
        },
        isShown(value) {
            if (value) {
                this.constructItems();
            }
        },
        items: {
            handler(newValue, oldValue) {
                this.constructItems();
            },
            deep: true,
        },
        value(newValue) {
            this.localValue = newValue;
        }
    },
    created() {
    },
    destroyed() {
    },
    mounted() {
        this.constructItems();
    },
})
</script>

<style lang="scss" scoped>

</style>